import React, { useState } from "react";
import { Download } from "lucide-react";

// Components
import HeaderBar from "./components/layout/HeaderBar";
import SidebarNav from "./components/layout/SidebarNav";

// Cards
import CampaignOverviewCard from "./components/cards/CampaignOverviewCard";
import LeadFlowStatusCard from "./components/cards/LeadFlowStatusCard";
import RegionScopeCard from "./components/cards/RegionScopeCard";
import SegmentationCard from "./components/cards/SegmentationCard";
import StreamsBuilderCard from "./components/cards/StreamsBuilderCard";
import MarketoBuildPackCard from "./components/cards/MarketoBuildPackCard";
import BestPracticeBlueprintsCard from "./components/cards/BestPracticeBlueprintsCard"; // NEW IMPORT

// Data
import campaignData from "./data/campaignData.json"; // Source of truth for content
import {
  initialStatus,
  segmentationOptions,
  defaultStreams,
} from "./data/nurtureDefaults"; // Keeping for legacy status widgets

const App = () => {
  // --- STATE MANAGEMENT ---

  // 1. Navigation State (Controls which tab is visible)
  const [activeView, setActiveView] = useState("overview");

  // 2. Widget State (Keeps the dashboard widgets alive)
  const [status, setStatus] = useState(initialStatus);
  const [activeRegions, setActiveRegions] = useState({
    India: true,
    Asia: true,
  });
  const [segments, setSegments] = useState([]);
  const [streams, setStreams] = useState(defaultStreams);

  // --- RENDER LOGIC ---

  // This function decides what to show based on the Sidebar click
  const renderContent = () => {
    switch (activeView) {
      case "overview":
        return (
          <div className="space-y-6">
            {/* The Dashboard View */}
            <CampaignOverviewCard data={campaignData} />

            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-12 lg:col-span-8">
                <LeadFlowStatusCard data={status.regions} />
              </div>
              <div className="col-span-12 lg:col-span-4">
                <RegionScopeCard
                  activeRegions={activeRegions}
                  toggleRegion={(region) =>
                    setActiveRegions({
                      ...activeRegions,
                      [region]: !activeRegions[region],
                    })
                  }
                />
              </div>
            </div>
          </div>
        );

      case "segmentation":
        return (
          <SegmentationCard
            data={campaignData}
            options={segmentationOptions}
            onGenerate={(newSeg) => setSegments([...segments, newSeg])}
          />
        );

      case "streams":
        return (
          <StreamsBuilderCard
            data={campaignData}
            segments={segments}
            existingStreams={streams}
            onUpdate={setStreams}
          />
        );

      case "blueprints":
        return <BestPracticeBlueprintsCard />; // The new component we just built

      case "export":
        return (
          <MarketoBuildPackCard
            data={campaignData}
            streams={streams}
            regions={activeRegions}
          />
        );

      default:
        return <CampaignOverviewCard data={campaignData} />;
    }
  };

  return (
    <div className="flex h-screen bg-slate-50 text-slate-800 font-sans">
      {/* LEFT SIDEBAR - NOW CONNECTED */}
      <SidebarNav activeView={activeView} onNavigate={setActiveView} />

      {/* MAIN CONTENT AREA */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* TOP HEADER */}
        <HeaderBar lastUpdated={status.lastUpdated} />

        {/* SCROLLABLE DASHBOARD */}
        <main className="flex-1 overflow-y-auto p-8">
          <div className="max-w-7xl mx-auto">
            {/* Page Header & Global Action */}
            <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-slate-900">
                  Nurture Optimization Center
                </h1>
                <p className="text-slate-500 mt-1">
                  Strategic frameworks, buyer-needs logic, and implementation
                  guides.
                </p>
              </div>
              <button className="bg-emerald-600 text-white px-4 py-2 rounded-md flex items-center gap-2 hover:bg-emerald-700 transition shadow-sm font-medium shrink-0">
                <Download size={18} /> Export Marketo-Ready Nurture Plan
              </button>
            </div>

            {/* DYNAMIC CONTENT AREA */}
            {renderContent()}
          </div>
        </main>
      </div>
    </div>
  );
};

export default App;
